import type { AxiosResponse } from "axios";
import { apiPathPrefix } from "../config/onPipeConfigurator/appConfig";
import type {
  BuildSpecification,
  BlastFrameCatalogueCarriageRow,
  BlastFrameCatalogueFrameSize,
  BlastFrameCatalogueKite,
  BlastFrameCatalogueVersion,
  CadCheck,
  ConfigurationResult,
  ConfigurationService,
  EngineeringOverride,
  EquipmentKind,
  InductionCoilCatalogueFrame,
  InductionCoilCatalogueVersion,
  OnPipeAssumptions,
  OnPipeCatalogue,
  ProjectSpecification,
  ValidationError,
} from "../components/OnPipeConfigurator/models/types";
import { DEFAULT_ON_PIPE_ASSUMPTIONS } from "../components/OnPipeConfigurator/domain/defaultAssumptions";
import axiosConfig from "./onPipeConfiguratorAxiosConfig";

const API = apiPathPrefix;

interface ApiEnvelope<T> {
  data: T;
  message: string;
}

export class OnPipeApiError extends Error {
  readonly status: number;

  readonly data: unknown;

  readonly validationErrors?: ValidationError[];

  constructor(status: number, message: string, data: unknown = null) {
    super(message);
    this.name = "OnPipeApiError";
    this.status = status;
    this.data = data;
    if (data && typeof data === "object" && "errors" in data) {
      const errors = (data as { errors?: ValidationError[] }).errors;
      if (Array.isArray(errors)) {
        this.validationErrors = errors;
      }
    }
  }
}

function unwrapApiPayload<T>(response: AxiosResponse): T {
  const body = response.data;
  if (body && typeof body === "object" && "data" in body && "message" in body) {
    return body.data as T;
  }
  return body as T;
}

async function apiGet<T>(path: string): Promise<T> {
  try {
    const response = await axiosConfig.get(`${API}${path}`, {
      headers: { Accept: "application/json" },
    });
    return unwrapApiPayload<T>(response);
  } catch (error) {
    throw toApiError(error);
  }
}

async function apiPost<T>(path: string, payload: unknown): Promise<T> {
  try {
    const response = await axiosConfig.post(`${API}${path}`, payload, {
      headers: { "Content-Type": "application/json", Accept: "application/json" },
    });
    return unwrapApiPayload<T>(response);
  } catch (error) {
    throw toApiError(error);
  }
}

async function apiPut<T>(path: string, payload: unknown): Promise<T> {
  try {
    const response = await axiosConfig.put(`${API}${path}`, payload, {
      headers: { "Content-Type": "application/json", Accept: "application/json" },
    });
    return unwrapApiPayload<T>(response);
  } catch (error) {
    throw toApiError(error);
  }
}

async function apiDelete<T = void>(path: string): Promise<T> {
  try {
    const response = await axiosConfig.delete(`${API}${path}`, {
      headers: { Accept: "application/json" },
    });
    if (response.status === 204 || response.data == null || response.data === "") {
      return undefined as T;
    }
    return unwrapApiPayload<T>(response);
  } catch (error) {
    throw toApiError(error);
  }
}

function toApiError(error: unknown): OnPipeApiError {
  if (error && typeof error === "object") {
    const axiosError = error as {
      response?: { status?: number; data?: ApiEnvelope<unknown> };
      message?: string;
      code?: string;
    };

    if (!axiosError.response && (axiosError.message === "Network Error" || axiosError.code === "ERR_NETWORK")) {
      return new OnPipeApiError(
        0,
        "Network Error — On-Pipe API is unreachable. Check REACT_APP_AWS_ON_PIPE_CONFIGURATOR_API (or that on-pipe-configurator-api is running where Coil points).",
      );
    }

    if ("response" in error && axiosError.response) {
      const status = axiosError.response.status ?? 500;
      const body = axiosError.response.data;
      const message =
        body && typeof body === "object" && "message" in body && typeof body.message === "string"
          ? body.message
          : axiosError.message ?? "Request failed.";
      const data = body && typeof body === "object" && "data" in body ? body.data : null;
      return new OnPipeApiError(status, message, data);
    }

    if (axiosError.message) {
      return new OnPipeApiError(500, axiosError.message);
    }
  }
  if (error instanceof Error) {
    return new OnPipeApiError(500, error.message);
  }
  return new OnPipeApiError(500, "Request failed.");
}

export const onPipeConfiguratorApi: ConfigurationService = {
  async listConfigurations() {
    return apiGet<ConfigurationResult[]>("/configurations");
  },

  async createConfiguration(project: ProjectSpecification) {
    return apiPost<ConfigurationResult>("/configurations", project);
  },

  async updateConfiguration(configurationId: string, project: ProjectSpecification) {
    return apiPut<ConfigurationResult>(
      `/configurations/${encodeURIComponent(configurationId)}`,
      project,
    );
  },

  async deleteConfiguration(configurationId: string) {
    await apiDelete(`/configurations/${encodeURIComponent(configurationId)}`);
  },

  async getConfiguration(configurationId: string) {
    return apiGet<ConfigurationResult>(`/configurations/${encodeURIComponent(configurationId)}`);
  },

  async getBuildSpecification(configurationId: string, equipment: EquipmentKind) {
    const query = new URLSearchParams({ equipment }).toString();
    return apiGet<BuildSpecification>(
      `/configurations/${encodeURIComponent(configurationId)}/build-spec?${query}`,
    );
  },

  async updateCadChecks(configurationId: string, equipment: EquipmentKind, checks: CadCheck[]) {
    return apiPut<ConfigurationResult>(
      `/configurations/${encodeURIComponent(configurationId)}/cad-checks`,
      { equipment, checks },
    );
  },

  async applyOverride(
    configurationId: string,
    equipment: EquipmentKind,
    override: EngineeringOverride,
  ) {
    return apiPost<ConfigurationResult>(
      `/configurations/${encodeURIComponent(configurationId)}/overrides`,
      { equipment, override },
    );
  },

  async getCatalogue() {
    return apiGet<OnPipeCatalogue>("/catalogue");
  },

  async createBlastFrameFrameSize(item: BlastFrameCatalogueFrameSize) {
    return apiPost<BlastFrameCatalogueFrameSize>("/catalogue/blast-frame/frame-sizes", item);
  },

  async createBlastFrameVersion(item: BlastFrameCatalogueVersion) {
    return apiPost<BlastFrameCatalogueVersion>("/catalogue/blast-frame/versions", item);
  },

  async createBlastFrameKite(item: BlastFrameCatalogueKite) {
    return apiPost<BlastFrameCatalogueKite>("/catalogue/blast-frame/kites", item);
  },

  async createBlastFrameCarriageRow(item: BlastFrameCatalogueCarriageRow) {
    return apiPost<BlastFrameCatalogueCarriageRow>("/catalogue/blast-frame/carriage-rows", item);
  },

  async deleteBlastFrameFrameSize(label: string) {
    await apiDelete(`/catalogue/blast-frame/frame-sizes/${encodeURIComponent(label)}`);
  },

  async deleteBlastFrameVersion(versionId: string) {
    await apiDelete(`/catalogue/blast-frame/versions/${encodeURIComponent(versionId)}`);
  },

  async deleteBlastFrameKite(kiteId: string) {
    await apiDelete(`/catalogue/blast-frame/kites/${encodeURIComponent(kiteId)}`);
  },

  async deleteBlastFrameCarriageRow(frameSize: string) {
    await apiDelete(`/catalogue/blast-frame/carriage-rows/${encodeURIComponent(frameSize)}`);
  },

  async createInductionCoilFrame(item: InductionCoilCatalogueFrame) {
    return apiPost<InductionCoilCatalogueFrame>("/catalogue/induction-coil/frames", item);
  },

  async createInductionCoilVersion(item: InductionCoilCatalogueVersion) {
    return apiPost<InductionCoilCatalogueVersion>("/catalogue/induction-coil/versions", item);
  },

  async deleteInductionCoilFrame(frameId: string) {
    await apiDelete(`/catalogue/induction-coil/frames/${encodeURIComponent(frameId)}`);
  },

  async deleteInductionCoilVersion(versionId: string) {
    await apiDelete(`/catalogue/induction-coil/versions/${encodeURIComponent(versionId)}`);
  },

  async getAssumptions() {
    // GET /assumptions is not available yet; only PUT is supported.
    // Return defaults so the UI can edit and save via updateAssumptions.
    return {
      blastFrame: { ...DEFAULT_ON_PIPE_ASSUMPTIONS.blastFrame },
      inductionCoil: { ...DEFAULT_ON_PIPE_ASSUMPTIONS.inductionCoil },
    };
  },

  async updateAssumptions(assumptions: OnPipeAssumptions) {
    return apiPut<OnPipeAssumptions>("/assumptions", assumptions);
  },
};
